/**
 * TCS shared Choose options isolation — PREVIEW, 2026-09-28.
 *
 * Applies to all marked Dawn quick-add modals emitted by card-product.liquid:
 * complementary, collection, search and featured product cards that use it.
 * No product-handle, SKU, brand, option-name or variant-ID allow-list.
 * Ordinary PDP forms and direct single-variant card buttons are untouched.
 *
 * Reads the chosen modal's native options, validates against that card's own
 * server-rendered variants, then submits exactly that variant and quantity.
 * Full-page fallback for detected app options, selling plans, backorders,
 * made-to-order, bundles or unsupported/ambiguous data: never silently strip
 * required order details to make an add succeed.
 */
(function () {
  'use strict';
  if (window.__tcsQuickAddGuardLoaded) return;
  window.__tcsQuickAddGuardLoaded = true;

  // Capture before external app scripts replace fetch/FormData or event handlers.
  var send = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  var selector = 'quick-add-modal[data-tcs-quick-add-guard="true"]';
  var pending = new WeakSet();
  var uncertain = new WeakSet();
  var requestInProgress = false;

  function localURL(path) {
    if (!path) throw new Error('Product link is unavailable.');
    var url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid store URL.');
    return url;
  }
  function cartURL() {
    return localURL((window.routes && window.routes.cart_url) ||
      ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart').href;
  }
  function productURL(modal, variantId) {
    var url = localURL(modal.getAttribute('data-tcs-product-url'));
    if (!/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product link.');
    if (variantId) url.searchParams.set('variant', String(variantId));
    return url.href;
  }
  function context(modal) {
    var element = Array.from(modal.children).find(function (child) {
      return child.matches('script[data-tcs-quick-add-context]');
    });
    var data;
    try { data = JSON.parse(element && element.textContent); } catch (error) { data = null; }
    if (!data || String(data.productId) !== modal.getAttribute('data-tcs-product-id') ||
        !Array.isArray(data.variants) || !data.variants.length ||
        !Array.isArray(data.optionNames) || !data.optionNames.length) {
      throw new Error('We could not verify this product. Please use its full product page.');
    }
    var ids = data.variants.map(function (variant) { return String(variant.id); });
    if (new Set(ids).size !== ids.length) throw new Error('Product options are ambiguous. Please use its product page.');
    return data;
  }
  function requiresFullPage(modal) {
    try { return context(modal).fullPageRequired === true; } catch (error) { return true; }
  }
  function openProductPage(modal, variantId) {
    try { window.location.assign(productURL(modal, variantId)); }
    catch (error) { console.warn('[TCS quick add]', error); }
  }
  window.TCSQuickAddGuard = Object.freeze({
    requiresFullPage: requiresFullPage,
    openProductPage: openProductPage
  });

  function message(form, modal, text, viewCart, variantId) {
    if (!form || !form.isConnected) return;
    var component = form.closest('product-form') || form;
    var wrapper = component.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');
      component.insertBefore(wrapper, component.firstChild);
    }
    var span = wrapper.querySelector('.product-form__error-message');
    if (!span) {
      span = document.createElement('span');
      span.className = 'product-form__error-message';
      wrapper.appendChild(span);
    }
    span.textContent = text + ' ';
    var link = document.createElement('a');
    link.className = 'link underlined-link';
    link.textContent = viewCart ? 'View cart' : 'Open product page';
    try { link.href = viewCart ? cartURL() : productURL(modal, variantId); }
    catch (error) { link.href = window.location.pathname; }
    span.appendChild(link);
    wrapper.hidden = false;
  }
  function clearMessage(form) {
    var wrapper = (form.closest('product-form') || form).querySelector('.product-form__error-message-wrapper');
    if (wrapper) wrapper.hidden = true;
  }
  function scopedElements(root, query) {
    return Array.from(root.querySelectorAll(query)).filter(function (element) {
      // Do not read forms in nested recommendations/another modal.
      return element.closest('product-info') === root ||
        (root.tagName.toLowerCase() !== 'product-info' && element.closest(selector) === root);
    });
  }
  function purchaseForm(form, modal) {
    if (!(form instanceof HTMLFormElement) || !modal.contains(form) ||
        !form.closest('product-form') || form.closest(selector) !== modal) return false;
    try { return /\/cart\/add(?:\.js)?\/?$/.test(localURL(form.getAttribute('action')).pathname); }
    catch (error) { return false; }
  }
  function extraPurchaseFlow(info, form) {
    // Scope ALL checks to this modal. A preorder main product behind it is irrelevant.
    var widgets = info.querySelector(
      '#bcpo, [id^="bcpo-"], .bcpo-virtual-options, [data-manta-configurator], ' +
      '.made-to-order-notice, [data-tcs-quick-add-full-page], [data-selling-plan], ' +
      '[class*="stoq"], [id*="stoq"], [class*="restock-rocket"], [id*="restock-rocket"], ' +
      '[class*="preorder"], [id*="preorder"]'
    );
    if (widgets) return true;
    return scopedElements(info, 'input, select, textarea').some(function (field) {
      var name = field.getAttribute('name') || '';
      // A separate purchase flow may require hidden fields even before a selection.
      return /^properties\[/.test(name) || name === 'selling_plan' || /^items\[/.test(name) ||
        name === 'parent_id' || name === 'parent_line_key' || field.type === 'file';
    });
  }
  function nativeOptions(picker, expected) {
    var groups = Array.from(picker.children).filter(function (child) {
      return child.matches('fieldset, .product-form__input');
    });
    if (groups.length !== expected) throw new Error('Please complete this product’s options on its product page.');
    return groups.map(function (group) {
      var select = group.querySelector('select');
      var selected = select || group.querySelector('input[type="radio"]:checked');
      if (!selected || selected.value === '') throw new Error('Please select every option before adding this item.');
      return selected.value;
    });
  }
  function selectedItem(modal, form) {
    var data = context(modal);
    var info = form.closest('product-info');
    if (!info || info.closest(selector) !== modal) {
      throw new Error('Please complete this purchase on the product page.');
    }
    // Check the freshly fetched popup really belongs to the clicked card.
    var displayedURL = info.getAttribute('data-url');
    if (!displayedURL || localURL(displayedURL).pathname.replace(/\/$/, '') !==
        localURL(productURL(modal)).pathname.replace(/\/$/, '')) {
      throw new Error('The opened product could not be verified. Nothing was added.');
    }
    if (data.fullPageRequired || extraPurchaseFlow(info, form)) {
      throw new Error('This product needs its full product page to preserve its required options or purchase terms. Nothing was added.');
    }
    var pickers = scopedElements(info, 'variant-radios, variant-selects');
    var variant;
    if (pickers.length === 1) {
      var picker = pickers[0];
      var pickerURL = picker.getAttribute('data-url');
      if (pickerURL && localURL(pickerURL).pathname !== localURL(displayedURL).pathname) {
        throw new Error('The options belong to a different product. Nothing was added.');
      }
      var values = nativeOptions(picker, data.optionNames.length);
      var raw = picker.querySelector('script[type="application/json"]');
      var currentVariants;
      try { currentVariants = JSON.parse(raw && raw.textContent); } catch (error) { currentVariants = null; }
      if (!Array.isArray(currentVariants)) throw new Error('The product options could not be verified. Please open its product page.');
      var matches = currentVariants.filter(function (v) {
        return Array.isArray(v.options) && v.options.length === values.length &&
          v.options.every(function (value, index) { return value === values[index]; });
      });
      if (matches.length !== 1) throw new Error('That combination is unavailable. Please select another combination.');
      variant = matches[0];
      var cardVariant = data.variants.find(function (v) { return String(v.id) === String(variant.id); });
      if (!cardVariant || JSON.stringify(cardVariant.options) !== JSON.stringify(variant.options)) {
        throw new Error('That variant could not be verified for this product. Nothing was added.');
      }
      if (cardVariant.fullPageRequired) {
        var fullPageError = new Error('Please complete this backorder or bundle on the product page so its purchase terms are included. Nothing was added.');
        fullPageError.variantId = variant.id;
        throw fullPageError;
      }
    } else if (!pickers.length && data.variants.length === 1 &&
               data.optionNames.length === 1 && data.optionNames[0] === 'Title' &&
               data.variants[0].options[0] === 'Default Title') {
      // Single/default variant opens a modal for a quantity rule; no option selector.
      variant = data.variants[0];
      if (variant.fullPageRequired) throw new Error('Please complete this purchase on the full product page.');
    } else {
      throw new Error('This product uses a different configurator. Please complete its options on its product page.');
    }
    if (!variant.available) throw new Error('The selected variant is currently unavailable.');
    var id = Number(variant.id);
    if (!Number.isSafeInteger(id) || id <= 0) throw new Error('The selected variant could not be verified. Nothing was added.');

    // Native quantity lives beside the form in Dawn, not necessarily inside it.
    var quantities = scopedElements(info, 'input[name="quantity"]');
    if (quantities.length > 1) throw new Error('The quantity is ambiguous. Please use the product page.');
    var input = quantities[0];
    var quantity = input ? Number(input.value) : 1;
    var rule = variant.quantity_rule || {};
    var minimum = input && input.hasAttribute('min') ? Number(input.min) : Number(rule.min || 1);
    var step = input && input.hasAttribute('step') ? Number(input.step) : Number(rule.increment || 1);
    var maximum = input && input.getAttribute('max') !== null && input.getAttribute('max') !== '' ?
      Number(input.max) : (rule.max == null ? null : Number(rule.max));
    if (!Number.isSafeInteger(quantity) || quantity <= 0 || !Number.isFinite(minimum) ||
        !Number.isFinite(step) || step <= 0 || quantity < minimum ||
        (quantity - minimum) % step !== 0 || (maximum !== null && quantity > maximum) ||
        (input && !input.checkValidity())) {
      throw new Error('Please enter a valid quantity for the selected item.');
    }
    // Honour native required fields even though Dawn adds novalidate to forms.
    var invalid = scopedElements(info, 'input, select, textarea').find(function (field) {
      return !field.disabled && field.willValidate && !field.checkValidity();
    });
    if (invalid) {
      invalid.reportValidity();
      throw new Error('Please complete the required field before adding this item.');
    }
    return { id: id, quantity: quantity };
  }
  function cartComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }
  function responseError(body) {
    if (body && typeof body.description === 'string') return body.description;
    if (body && typeof body.message === 'string') return body.message;
    return 'The selected item could not be added. Please check its product page.';
  }
  function showCart(modal, form, cart, response, sectionIDs) {
    var canRender = cart && typeof cart.renderContents === 'function' && sectionIDs.length &&
      response.sections && sectionIDs.every(function (id) { return typeof response.sections[id] === 'string'; });
    if (!canRender) { window.location.assign(cartURL()); return; }
    var opener = modal.openedBy || document.querySelector('#cart-icon-bubble');
    if (typeof cart.setActiveElement === 'function') cart.setActiveElement(opener);
    if (form.isConnected && modal.contains(form) && typeof modal.hide === 'function') modal.hide(true);
    window.setTimeout(function () {
      try {
        cart.classList.remove('is-empty');
        cart.renderContents(response);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tcs-quick-add-guard' }); }
          catch (error) { console.warn('[TCS quick add] Cart subscriber failed:', error); }
        }
      } catch (error) {
        console.warn('[TCS quick add] Opening cart instead:', error);
        window.location.assign(cartURL());
      }
    }, 0);
  }
  async function addItem(modal, form, button) {
    if (pending.has(modal) || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    if (uncertain.has(modal)) {
      message(form, modal, 'Please check your cart and reload this page before retrying; the previous request may already have succeeded.', true);
      return;
    }
    if (requestInProgress) {
      message(form, modal, 'Please wait for the other quick-add request to finish.', false);
      return;
    }
    clearMessage(form);
    var item;
    try { item = selectedItem(modal, form); }
    catch (error) { message(form, modal, error.message, false, error.variantId); return; }
    if (!send) { message(form, modal, 'Please use the product page to add this item.', false); return; }
    pending.add(modal);
    requestInProgress = true;
    var oldDisabled = button.getAttribute('aria-disabled');
    var oldBusy = button.getAttribute('aria-busy');
    var oldLoading = button.classList.contains('loading');
    var spinner = button.querySelector('.loading-overlay__spinner');
    var spinnerWasHidden = spinner ? spinner.classList.contains('hidden') : true;
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');

    var cart = cartComponent();
    var sections = [];
    if (cart && typeof cart.getSectionsToRender === 'function') {
      try { sections = cart.getSectionsToRender().map(function (s) { return s.id; }).slice(0, 5); }
      catch (error) { sections = []; }
    }
    // Explicit identity: never serialize window-level app state or the parent form.
    var payload = { id: item.id, quantity: item.quantity };
    if (sections.length) { payload.sections = sections; payload.sections_url = window.location.pathname; }
    var sent = false;
    try {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = localURL((window.routes && window.routes.cart_add_url) || root + 'cart/add.js');
      if (!/\/cart\/add(?:\.js)?$/.test(url.pathname)) throw new Error('Invalid cart endpoint.');
      if (!url.pathname.endsWith('.js')) url.pathname += '.js';
      sent = true;
      var http = await send(url.href, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
      var response = await http.json();
      if (!http.ok || response.status) {
        // Do not auto-retry: even a rejected/partial quantity request merits a cart check.
        message(form, modal, responseError(response) + ' Check your cart before retrying.', true);
        if (http.status >= 500) uncertain.add(modal);
        return;
      }
      var added = Array.isArray(response.items) ? response.items[0] : response;
      if (!added || String(added.variant_id || added.id) !== String(item.id) ||
          String(added.product_id) !== modal.getAttribute('data-tcs-product-id') ||
          (Array.isArray(response.items) && response.items.length !== 1)) {
        uncertain.add(modal);
        message(form, modal, 'The cart response did not match this product and variant. Please check your cart before continuing.', true);
        return;
      }
      showCart(modal, form, cart, Object.assign({}, added, { sections: response.sections }), sections);
    } catch (error) {
      if (sent) uncertain.add(modal);
      console.warn('[TCS quick add] Could not confirm cart update:', error);
      message(form, modal, sent ? 'We could not confirm the cart update. Check your cart before trying again.' :
        'The item could not be added here. Please use its product page.', sent);
    } finally {
      pending.delete(modal);
      requestInProgress = false;
      if (oldDisabled === null) button.removeAttribute('aria-disabled'); else button.setAttribute('aria-disabled', oldDisabled);
      if (oldBusy === null) button.removeAttribute('aria-busy'); else button.setAttribute('aria-busy', oldBusy);
      if (!oldLoading) button.classList.remove('loading');
      if (spinner && spinnerWasHidden) spinner.classList.add('hidden');
    }
  }
  function interceptClick(event) {
    if (!(event.target instanceof Element)) return;
    var button = event.target.closest('button[type="submit"], input[type="submit"], .product-form__submit');
    if (!button) return;
    var modal = button.closest(selector);
    if (!modal || !modal.hasAttribute('open')) return;
    var form = button.closest('form');
    if (!form && button.getAttribute('form')) {
      // Resolve by ID INSIDE this modal, never document.getElementById.
      form = Array.from(modal.querySelectorAll('product-form form')).find(function (f) {
        return f.id === button.getAttribute('form');
      });
    }
    if (!purchaseForm(form, modal)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void addItem(modal, form, button);
  }
  function interceptSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var modal = form.closest(selector);
    if (!modal || !modal.hasAttribute('open') || !purchaseForm(form, modal)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var button = event.submitter || form.querySelector('button[type="submit"], input[type="submit"]');
    if (button && modal.contains(button)) void addItem(modal, form, button);
  }
  // Registered before app embeds. All events outside marked modal forms pass through.
  window.addEventListener('click', interceptClick, true);
  window.addEventListener('submit', interceptSubmit, true);
})();
